import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import * as Sentry from "@sentry/react";

export type UserRole = 'super_admin' | 'administrator' | 'business_owner' | 'business_admin' | 'successor' | 'guest' | 'consultant_internal' | 'consultant_external';

export function useUserRole(organizationId?: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['user-role', user?.id, organizationId],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase.rpc('get_user_role_in_org', {
        target_org_id: organizationId || null
      });

      if (error) {
        Sentry.captureException(error, {
          tags: { context: 'role_error', operation: 'user_role', action: 'fetch' },
          extra: { message: 'Error fetching user role', organizationId }
        });
        throw error;
      }

      return data as UserRole | null;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useCanDeleteProfile(targetUserId?: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['can-delete-profile', user?.id, targetUserId],
    queryFn: async () => {
      if (!user?.id || !targetUserId) return false;

      const { data, error } = await supabase.rpc('can_delete_profile', {
        target_user_id: targetUserId
      });

      if (error) {
        Sentry.captureException(error, {
          tags: { context: 'role_error', operation: 'user_role', action: 'check_delete' },
          extra: { message: 'Error checking delete permission', targetUserId }
        });
        return false;
      }

      return !!data;
    },
    enabled: !!user?.id && !!targetUserId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}